import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import PremiumPrompt from './PremiumPrompt';
import { useAuth, getToken } from '../context/AuthContext';
import config from '../config';

  const { apiBaseURL } = config;

  // Helper function to construct API URLs
  export const getApiUrl = (endpoint) => {
    return `${apiBaseURL}${endpoint}`;
  };

  const ScreenersPage = ({ initialScreener = null, onStockSelect }) => {
  const { offerOptionsScreener, premiumFeaturesEnabled } = useFlags();
  const { isSubscribed } = useAuth();
  const canAccessCsp = premiumFeaturesEnabled || isSubscribed;

  const [activeScreener, setActiveScreener] = useState(initialScreener || 'top-value');
  const [indexMode, setIndexMode] = useState('russell-2000'); // 'all', 'russell-2000', or 'sp500'
  const [screenerData, setScreenerData] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});

  const [cspData, setCspData] = useState(null);
  const [cspLoading, setCspLoading] = useState(false);
  const [cspError, setCspError] = useState(null);
  const cspFetchedRef = useRef(false);

  const [pdsData, setPdsData] = useState(null);
  const [pdsLoading, setPdsLoading] = useState(false);
  const [pdsError, setPdsError] = useState(null);
  const pdsFetchedRef = useRef(false);

  const [ccData, setCcData] = useState(null);
  const [ccLoading, setCcLoading] = useState(false);
  const [ccError, setCcError] = useState(null);
  const ccFetchedRef = useRef(false);

  const [wheelData, setWheelData] = useState(null);
  const [wheelLoading, setWheelLoading] = useState(false);
  const [wheelError, setWheelError] = useState(null);
  const wheelFetchedRef = useRef(false);

  const [straddleData, setStraddleData] = useState(null);
  const [straddleLoading, setStraddleLoading] = useState(false);
  const [straddleError, setStraddleError] = useState(null);
  const straddleFetchedRef = useRef(false);

  const OPTIONS_SCREENERS = ['csp', 'pds', 'cc', 'wheel', 'straddle'];
  const isOptionsMode = OPTIONS_SCREENERS.includes(activeScreener);

  // Use ref to track if data has been fetched
  const fetchedScreeners = useRef({});

  // Wrap the screeners array in useMemo to prevent recreating it on every render
  const screeners = useMemo(() => [
    { id: 'top-value', name: 'ValueSieve Top 100', endpoint: '/api/screener/top-value', russellEndpoint: '/api/russell-2000/top-value', sp500Endpoint: '/api/sp500/top-value' },
    { id: 'implied-growth', name: 'Implied Growth', endpoint: '/api/screener/implied-growth', russellEndpoint: '/api/russell-2000/implied-growth', sp500Endpoint: '/api/sp500/implied-growth' },
    { id: 'magic-formula', name: 'Magic Formula', endpoint: '/api/screener/magic-formula', russellEndpoint: '/api/russell-2000/magic-formula', sp500Endpoint: '/api/sp500/magic-formula' },
    { id: 'dogs-of-dow', name: 'Dogs of the Dow', endpoint: '/api/screener/dogs-of-dow' },
    { id: 'net-net', name: 'Net-Net Stocks', endpoint: '/api/screener/net-net', russellEndpoint: '/api/russell-2000/net-net', sp500Endpoint: '/api/sp500/net-net' },
    { id: 'growth-exceeds-pe', name: 'Growth Exceeds P/E', endpoint: '/api/screener/growth-exceeds-pe', russellEndpoint: '/api/russell-2000/growth-exceeds-pe', sp500Endpoint: '/api/sp500/growth-exceeds-pe' },
    { id: 'roic', name: 'Return on Invested Capital', endpoint: '/api/screener/roic', russellEndpoint: '/api/russell-2000/roic', sp500Endpoint: '/api/sp500/roic' },
    { id: 'peg-ratio', name: 'PEG Ratio', endpoint: '/api/screener/peg-ratio', russellEndpoint: '/api/russell-2000/peg-ratio', sp500Endpoint: '/api/sp500/peg-ratio' },
  ], []); // Empty dependency array means this array is created only once

  // Update active screener when initialScreener prop changes
  useEffect(() => {
    if (initialScreener) {
      setActiveScreener(initialScreener);
      
      // Check if this is a Russell-only screener or a Russell tag
      const screener = screeners.find(s => s.id === initialScreener);
      if (screener && screener.russellOnly) {
        setIndexMode('russell-2000');
      } else if (initialScreener.includes('russell-2000') || initialScreener === 'russell-movers') {
        // If it's a Russell tag, switch to Russell 2000 mode
        setIndexMode('russell-2000');
      }
    }
  }, [initialScreener, screeners]);
  
  // Get currently visible screeners based on index mode
  const visibleScreeners = useMemo(() => {
    if (indexMode === 'all') {
      return screeners.filter(s => !s.russellOnly);
    } else if (indexMode === 'russell-2000') {
      return screeners.filter(s => s.russellEndpoint || s.russellOnly);
    } else if (indexMode === 'sp500') {
      return screeners.filter(s => s.sp500Endpoint);
    }
    return screeners;
  }, [screeners, indexMode]);

  // Handle click on a stock
  const handleStockClick = useCallback((symbol) => {
    if (onStockSelect && symbol) {
      onStockSelect(symbol);
    }
  }, [onStockSelect]);
  
  // Generate a unique cache key for a screener based on its ID and the current index mode
  const getScreenerCacheKey = useCallback((screenerId) => {
    return `${screenerId}-${indexMode}`;
  }, [indexMode]);
  
  // Using useCallback to memoize the fetchScreenerData function
  const fetchScreenerData = useCallback(async (screenerId) => {
    const screener = screeners.find(s => s.id === screenerId);
    if (!screener) return;
    
    // Determine the endpoint to use based on the index mode
    let endpoint;
    if (indexMode === 'russell-2000' && (screener.russellEndpoint || screener.russellOnly)) {
      endpoint = screener.russellEndpoint || screener.endpoint;
    } else if (indexMode === 'sp500' && screener.sp500Endpoint) {
      endpoint = screener.sp500Endpoint;
    } else if (indexMode === 'all' || !screener.russellOnly) {
      endpoint = screener.endpoint;
    } else {
      console.error(`Screener ${screenerId} is not available in the selected index mode`);
      return;
    }
    
    const cacheKey = getScreenerCacheKey(screenerId);

    // Important change: Don't fetch if we've already fetched or are currently loading
    if (fetchedScreeners.current[cacheKey] || loading[cacheKey]) {
      return;
    }

    setLoading(prev => ({ ...prev, [cacheKey]: true }));
    setError(prev => ({ ...prev, [cacheKey]: null }));

    try {
      const response = await fetch(`${apiBaseURL}${endpoint}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch ${screener.name} data: ${errorText}`);
      }
      
      const data = await response.json();
      console.log(`${screener.name} data for ${indexMode}:`, data);
      
      setScreenerData(prev => ({ 
        ...prev, 
        [cacheKey]: data 
      }));
      
      // Mark this screener as fetched for this index mode
      fetchedScreeners.current[cacheKey] = true;
    } catch (err) {
      console.error(`Error fetching ${screener.name} data for ${indexMode}:`, err);
      setError(prev => ({ 
        ...prev, 
        [cacheKey]: err.message 
      }));
    } finally {
      setLoading(prev => ({ ...prev, [cacheKey]: false }));
    }
  }, [screeners, loading, getScreenerCacheKey, indexMode]);

  // Fetch data for the active screener when it changes or when index mode changes
  useEffect(() => {
    if (!isOptionsMode) fetchScreenerData(activeScreener);
  }, [activeScreener, fetchScreenerData, indexMode, isOptionsMode]);

  // Fetch CSP screener data (premium, auth-gated)
  useEffect(() => {
    if (activeScreener !== 'csp' || !canAccessCsp || cspFetchedRef.current) return;
    cspFetchedRef.current = true;

    let cancelled = false;
    setCspLoading(true);
    setCspError(null);

    fetch(`${apiBaseURL}/api/options/csp-screener`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        if (!res.ok) return res.json().then(b => { throw new Error(b.error || `HTTP ${res.status}`); });
        return res.json();
      })
      .then(data => { if (!cancelled) setCspData(data); })
      .catch(err => {
        if (!cancelled) setCspError(err.message);
        cspFetchedRef.current = false;
      })
      .finally(() => { if (!cancelled) setCspLoading(false); });

    return () => { cancelled = true; };
  }, [activeScreener, canAccessCsp]);

  // Fetch PDS screener data (premium, auth-gated)
  useEffect(() => {
    if (activeScreener !== 'pds' || !canAccessCsp || pdsFetchedRef.current) return;
    pdsFetchedRef.current = true;

    let cancelled = false;
    setPdsLoading(true);
    setPdsError(null);

    fetch(`${apiBaseURL}/api/options/pds-screener`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        if (!res.ok) return res.json().then(b => { throw new Error(b.error || `HTTP ${res.status}`); });
        return res.json();
      })
      .then(data => { if (!cancelled) setPdsData(data); })
      .catch(err => {
        if (!cancelled) setPdsError(err.message);
        pdsFetchedRef.current = false;
      })
      .finally(() => { if (!cancelled) setPdsLoading(false); });

    return () => { cancelled = true; };
  }, [activeScreener, canAccessCsp]);

  // Fetch Covered Call screener data (premium, auth-gated)
  useEffect(() => {
    if (activeScreener !== 'cc' || !canAccessCsp || ccFetchedRef.current) return;
    ccFetchedRef.current = true;

    let cancelled = false;
    setCcLoading(true);
    setCcError(null);

    fetch(`${apiBaseURL}/api/options/cc-screener`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        if (!res.ok) return res.json().then(b => { throw new Error(b.error || `HTTP ${res.status}`); });
        return res.json();
      })
      .then(data => { if (!cancelled) setCcData(data); })
      .catch(err => {
        if (!cancelled) setCcError(err.message);
        ccFetchedRef.current = false;
      })
      .finally(() => { if (!cancelled) setCcLoading(false); });

    return () => { cancelled = true; };
  }, [activeScreener, canAccessCsp]);

  // Fetch Wheel screener data (premium, auth-gated)
  useEffect(() => {
    if (activeScreener !== 'wheel' || !canAccessCsp || wheelFetchedRef.current) return;
    wheelFetchedRef.current = true;

    let cancelled = false;
    setWheelLoading(true);
    setWheelError(null);

    fetch(`${apiBaseURL}/api/options/wheel-screener`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        if (!res.ok) return res.json().then(b => { throw new Error(b.error || `HTTP ${res.status}`); });
        return res.json();
      })
      .then(data => { if (!cancelled) setWheelData(data); })
      .catch(err => {
        if (!cancelled) setWheelError(err.message);
        wheelFetchedRef.current = false;
      })
      .finally(() => { if (!cancelled) setWheelLoading(false); });

    return () => { cancelled = true; };
  }, [activeScreener, canAccessCsp]);

  // Fetch Long Straddle screener data (premium, auth-gated)
  useEffect(() => {
    if (activeScreener !== 'straddle' || !canAccessCsp || straddleFetchedRef.current) return;
    straddleFetchedRef.current = true;

    let cancelled = false;
    setStraddleLoading(true);
    setStraddleError(null);

    fetch(`${apiBaseURL}/api/options/straddle-screener`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        if (!res.ok) return res.json().then(b => { throw new Error(b.error || `HTTP ${res.status}`); });
        return res.json();
      })
      .then(data => { if (!cancelled) setStraddleData(data); })
      .catch(err => {
        if (!cancelled) setStraddleError(err.message);
        straddleFetchedRef.current = false;
      })
      .finally(() => { if (!cancelled) setStraddleLoading(false); });

    return () => { cancelled = true; };
  }, [activeScreener, canAccessCsp]);

  const RATING_COLOR = { Buy: '#27ae60', Hold: '#e67e22', Sell: '#e74c3c' };

  const renderTopValue = (data) => {
    if (!data || !data.stocks || data.stocks.length === 0) {
      return <div className="no-data-message">No value scores calculated yet. Check back shortly.</div>;
    }
    return (
      <div className="stock-table-container">
        <h3>{data.strategy}</h3>
        <p className="screener-description">{data.description}</p>
        <table className="stock-table clickable-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Symbol</th>
              <th>Name</th>
              <th>Price</th>
              <th>Market Cap</th>
              <th>Value Score</th>
              <th>Label</th>
              <th>Rating</th>
              <th>Growth</th>
              <th>Debt</th>
              <th>Cash</th>
            </tr>
          </thead>
          <tbody>
            {data.stocks.map(stock => (
              <tr key={stock.symbol} onClick={() => handleStockClick(stock.symbol)} className="clickable-row">
                <td style={{ color: '#aaa', fontSize: '12px' }}>{stock.rank}</td>
                <td style={{ fontWeight: '700' }}>{stock.symbol}</td>
                <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stock.name}</td>
                <td>{stock.price != null ? `$${stock.price.toFixed(2)}` : 'N/A'}</td>
                <td>{stock.market_cap}</td>
                <td style={{ fontWeight: '700', color: stock.color }}>{stock.value_score.toFixed(1)}</td>
                <td style={{ fontSize: '12px', color: stock.color }}>{stock.label}</td>
                <td>
                  <span style={{
                    fontSize: '12px', fontWeight: '700', color: '#fff',
                    background: RATING_COLOR[stock.rating] || '#999',
                    borderRadius: '4px', padding: '2px 8px',
                  }}>{stock.rating}</span>
                </td>
                <td style={{ color: '#666', fontSize: '13px' }}>{stock.growth_score != null ? stock.growth_score.toFixed(1) : 'N/A'}</td>
                <td style={{ color: '#666', fontSize: '13px' }}>{stock.debt_score != null ? stock.debt_score.toFixed(1) : 'N/A'}</td>
                <td style={{ color: '#666', fontSize: '13px' }}>{stock.cash_score != null ? stock.cash_score.toFixed(1) : 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Helper function to render a stock table based on the screener type
  const renderStockTable = (screenerId, data) => {
    if (!data || !data.stocks || data.stocks.length === 0) {
      return <div className="no-data-message">No stocks found for this screener.</div>;
    }

    // Get the keys for table headers, excluding some common non-display fields
    const excludeKeys = ['id', 'stock_id'];
    const stock = data.stocks[0];
    const keys = Object.keys(stock).filter(key => !excludeKeys.includes(key));

    // Format value based on key name and type
    const formatValue = (key, value) => {
      if (value === null || value === undefined) return 'N/A';
      
      // Format based on key name patterns
      if (key.toLowerCase().includes('price') || key.toLowerCase().includes('value')) {
        return typeof value === 'number' ? `$${value.toFixed(2)}` : value;
      }
      if (key.toLowerCase().includes('percent') || key.toLowerCase().includes('ratio') || 
          key.toLowerCase().includes('margin') || key.toLowerCase().includes('growth') || 
          key.toLowerCase().includes('yield')) {
        return typeof value === 'number' ? `${value.toFixed(2)}%` : value;
      }
      if (key.toLowerCase().includes('market') && key.toLowerCase().includes('cap')) {
        return typeof value === 'number' ? formatMarketCap(value) : value;
      }
      
      // Default formatting for numbers
      return typeof value === 'number' ? value.toFixed(2) : value;
    };

    // Format market cap with B/M suffix
    const formatMarketCap = (value) => {
      if (value >= 1000000000) {
        return `$${(value / 1000000000).toFixed(2)}B`;
      } else if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(2)}M`;
      } else {
        return `$${value.toFixed(2)}`;
      }
    };

    // Format header labels to be more readable
    const formatHeader = (key) => {
      return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    return (
      <div className="stock-table-container">
        <h3>{data.strategy || `${data.count || data.stocks.length} Stocks`}</h3>
        {data.description && <p className="screener-description">{data.description}</p>}
        {data.methodology && <p className="screener-methodology"><strong>Methodology:</strong> {data.methodology}</p>}
        {data.formula && <p className="screener-formula"><strong>Formula:</strong> {data.formula}</p>}
        
        {indexMode === 'russell-2000' && <div className="russell-badge">Russell 2000</div>}
        {indexMode === 'sp500' && <div className="russell-badge">S&amp;P 500</div>}
        
        <table className="stock-table clickable-table">
          <thead>
            <tr>
              {keys.map(key => (
                <th key={key}>{formatHeader(key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.stocks.map((stock, index) => (
              <tr 
                key={index} 
                onClick={() => handleStockClick(stock.symbol)}
                className="clickable-row"
                title={`View ${stock.name || stock.symbol} details`}
              >
                {keys.map(key => (
                  <td key={key}>{formatValue(key, stock[key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCspScreener = (data) => {
    if (!data || !data.results || data.results.length === 0) {
      return <div className="no-data-message">No stocks currently meet the CSP screener criteria. Check back after market hours.</div>;
    }

    const formatExp = (dateStr, expDte) => {
      const d = new Date(dateStr + 'T00:00:00');
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${label} (${expDte}d)`;
    };

    const cspScoreColor = (score) => {
      if (score >= 75) return '#27ae60';
      if (score >= 55) return '#f39c12';
      return '#e74c3c';
    };

    return (
      <div className="stock-table-container">
        <h3>Cash Secured Puts — S&amp;P 500</h3>
        <p className="screener-description">
          Top S&amp;P 500 candidates for selling cash-secured puts: fundamentally sound companies
          trading in the lower half of their Bollinger Band with elevated historical volatility.
          Premiums estimated via Black-Scholes using 30-day HV.
        </p>
        <div className="russell-badge">S&amp;P 500</div>
        <table className="stock-table clickable-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Symbol</th>
              <th>Company</th>
              <th>Price</th>
              <th>CSP Score</th>
              <th>VS Score</th>
              <th>HV Rank</th>
              <th>%B</th>
              <th>Strike</th>
              <th>Expiration</th>
              <th>Est. Premium</th>
              <th>Ann. Yield</th>
              <th>Breakeven</th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((r, i) => (
              <tr key={r.symbol} onClick={() => handleStockClick(r.symbol)} className="clickable-row">
                <td style={{ color: '#aaa', fontSize: '12px' }}>{i + 1}</td>
                <td style={{ fontWeight: '700' }}>{r.symbol}</td>
                <td style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</td>
                <td>${r.currentPrice.toFixed(2)}</td>
                <td>
                  <span className="csp-score-badge" style={{ background: cspScoreColor(r.cspScore) }}>
                    {r.cspScore}
                  </span>
                </td>
                <td>{r.valueSieveScore.toFixed(1)}</td>
                <td>{r.hvRank}th %ile</td>
                <td>{r.percentB.toFixed(2)}</td>
                <td>${r.suggestedStrike.toFixed(2)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatExp(r.suggestedExpiration, r.dte)}</td>
                <td>${r.estimatedPremium.toFixed(2)}</td>
                <td style={{ color: '#27ae60', fontWeight: '600' }}>{r.estimatedPremiumYieldAnnualized.toFixed(1)}%</td>
                <td>${r.breakeven.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
          Generated {new Date(data.generatedAt).toLocaleString()} · Strike ≈ 10% OTM · Not financial advice
        </p>
      </div>
    );
  };

  const renderPdsScreener = (data) => {
    if (!data || !data.results || data.results.length === 0) {
      return <div className="no-data-message">No stocks currently meet the Put Debit Spread screener criteria. Check back after market hours.</div>;
    }

    const formatExp = (dateStr, expDte) => {
      const d = new Date(dateStr + 'T00:00:00');
      return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (${expDte}d)`;
    };

    const pdsScoreColor = (score) => {
      if (score >= 75) return '#c0392b';
      if (score >= 55) return '#e67e22';
      return '#7f8c8d';
    };

    return (
      <div className="stock-table-container">
        <h3>Put Debit Spreads — S&amp;P 500</h3>
        <p className="screener-description">
          S&amp;P 500 stocks in the upper half of their Bollinger Band, extended above their 50-day MA,
          and showing fundamental weakness — bearish mean-reversion candidates for a put debit spread.
          Buy the ATM put, sell the ~10% OTM put. Premiums estimated via Black-Scholes using 30-day HV.
        </p>
        <div className="russell-badge">S&amp;P 500</div>
        <table className="stock-table clickable-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Symbol</th>
              <th>Company</th>
              <th>Price</th>
              <th>PDS Score</th>
              <th>VS Score</th>
              <th>HV Rank</th>
              <th>%B</th>
              <th>% Above MA50</th>
              <th>Long Strike</th>
              <th>Short Strike</th>
              <th>Expiration</th>
              <th>Net Debit</th>
              <th>Max Profit</th>
              <th>R/R</th>
              <th>Breakeven</th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((r, i) => (
              <tr key={r.symbol} onClick={() => handleStockClick(r.symbol)} className="clickable-row">
                <td style={{ color: '#aaa', fontSize: '12px' }}>{i + 1}</td>
                <td style={{ fontWeight: '700' }}>{r.symbol}</td>
                <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</td>
                <td>${r.currentPrice.toFixed(2)}</td>
                <td>
                  <span className="csp-score-badge" style={{ background: pdsScoreColor(r.pdsScore) }}>
                    {r.pdsScore}
                  </span>
                </td>
                <td>{r.valueSieveScore.toFixed(1)}</td>
                <td>{r.hvRank}th %ile</td>
                <td>{r.percentB.toFixed(2)}</td>
                <td style={{ color: '#e74c3c' }}>+{r.priceAboveMa50Pct.toFixed(1)}%</td>
                <td>${r.longStrike.toFixed(0)}</td>
                <td>${r.shortStrike.toFixed(0)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatExp(r.suggestedExpiration, r.dte)}</td>
                <td style={{ color: '#e74c3c' }}>${r.netDebit.toFixed(2)}</td>
                <td style={{ color: '#27ae60', fontWeight: '600' }}>${r.maxProfit.toFixed(2)}</td>
                <td style={{ fontWeight: '600' }}>{r.riskReward != null ? `${r.riskReward.toFixed(1)}x` : 'N/A'}</td>
                <td>${r.breakeven.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
          Generated {new Date(data.generatedAt).toLocaleString()} · Long strike ≈ ATM, short strike ≈ 10% OTM · Not financial advice
        </p>
      </div>
    );
  };

  const renderCcScreener = (data) => {
    if (!data || !data.results || data.results.length === 0) {
      return <div className="no-data-message">No stocks currently meet the Covered Call screener criteria. Check back after market hours.</div>;
    }

    const formatExp = (dateStr, expDte) => {
      const d = new Date(dateStr + 'T00:00:00');
      return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (${expDte}d)`;
    };

    const ccScoreColor = (score) => {
      if (score >= 75) return '#27ae60';
      if (score >= 55) return '#f39c12';
      return '#7f8c8d';
    };

    return (
      <div className="stock-table-container">
        <h3>Covered Calls — S&amp;P 500</h3>
        <p className="screener-description">
          Quality S&amp;P 500 stocks with elevated historical volatility ranked by covered call income potential.
          Strike set at the upper Bollinger Band — natural resistance where upside is limited.
          Premiums estimated via Black-Scholes using 30-day HV.
        </p>
        <div className="russell-badge">S&amp;P 500</div>
        <table className="stock-table clickable-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Symbol</th>
              <th>Company</th>
              <th>Price</th>
              <th>CC Score</th>
              <th>VS Score</th>
              <th>HV Rank</th>
              <th>%B</th>
              <th>Strike</th>
              <th>OTM %</th>
              <th>Expiration</th>
              <th>Est. Premium</th>
              <th>Ann. Yield</th>
              <th>If Called</th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((r, i) => (
              <tr key={r.symbol} onClick={() => handleStockClick(r.symbol)} className="clickable-row">
                <td style={{ color: '#aaa', fontSize: '12px' }}>{i + 1}</td>
                <td style={{ fontWeight: '700' }}>{r.symbol}</td>
                <td style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</td>
                <td>${r.currentPrice.toFixed(2)}</td>
                <td>
                  <span className="csp-score-badge" style={{ background: ccScoreColor(r.ccScore) }}>
                    {r.ccScore}
                  </span>
                </td>
                <td>{r.valueSieveScore.toFixed(1)}</td>
                <td>{r.hvRank}th %ile</td>
                <td>{r.percentB.toFixed(2)}</td>
                <td>${r.suggestedStrike.toFixed(2)}</td>
                <td style={{ color: '#888' }}>+{r.strikeOtmPct.toFixed(1)}%</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatExp(r.suggestedExpiration, r.dte)}</td>
                <td>${r.estimatedPremium.toFixed(2)}</td>
                <td style={{ color: '#27ae60', fontWeight: '600' }}>{r.annualizedYield.toFixed(1)}%</td>
                <td style={{ color: '#2980b9', fontWeight: '600' }}>{r.ifCalledReturn != null ? `+${r.ifCalledReturn.toFixed(1)}%` : 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
          Generated {new Date(data.generatedAt).toLocaleString()} · Strike = upper Bollinger Band · Not financial advice
        </p>
      </div>
    );
  };

  const renderWheelScreener = (data) => {
    if (!data || !data.results || data.results.length === 0) {
      return <div className="no-data-message">No stocks currently meet the Wheel screener criteria. Check back after market hours.</div>;
    }

    const wheelScoreColor = (score) => {
      if (score >= 75) return '#8e44ad';
      if (score >= 55) return '#2980b9';
      return '#7f8c8d';
    };

    const legColor = (leg) => {
      if (leg === 'Sell CSP') return { color: '#27ae60', fontWeight: '600' };
      if (leg === 'Sell CC')  return { color: '#e67e22', fontWeight: '600' };
      return { color: '#7f8c8d' };
    };

    return (
      <div className="stock-table-container">
        <h3>Wheel Strategy — S&amp;P 500</h3>
        <p className="screener-description">
          Quality S&amp;P 500 stocks ranked for full-cycle wheel trading: sell a cash-secured put, get assigned,
          then sell covered calls until called away — repeat. Ranked by consistent HV, fundamental quality,
          and BB centrality (room to run both legs). <strong>Cycles to $0</strong> = how many complete wheels
          until total premium collected equals today's stock price.
        </p>
        <div className="russell-badge">S&amp;P 500</div>
        <table className="stock-table clickable-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Symbol</th>
              <th>Company</th>
              <th>Price</th>
              <th>Wheel Score</th>
              <th>VS Score</th>
              <th>HV Rank</th>
              <th>%B</th>
              <th>Current Leg</th>
              <th>CSP Strike</th>
              <th>CSP Yield</th>
              <th>CC Strike</th>
              <th>CC Yield</th>
              <th>Cycle Yield</th>
              <th>Cycles to $0</th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((r, i) => (
              <tr key={r.symbol} onClick={() => handleStockClick(r.symbol)} className="clickable-row">
                <td style={{ color: '#aaa', fontSize: '12px' }}>{i + 1}</td>
                <td style={{ fontWeight: '700' }}>{r.symbol}</td>
                <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</td>
                <td>${r.currentPrice.toFixed(2)}</td>
                <td>
                  <span className="csp-score-badge" style={{ background: wheelScoreColor(r.wheelScore) }}>
                    {r.wheelScore}
                  </span>
                </td>
                <td>{r.valueSieveScore.toFixed(1)}</td>
                <td>{r.hvRank}th %ile</td>
                <td>{r.percentB.toFixed(2)}</td>
                <td><span style={legColor(r.currentLeg)}>{r.currentLeg}</span></td>
                <td>${r.cspStrike.toFixed(2)}</td>
                <td style={{ color: '#27ae60' }}>{r.cspAnnualizedYield.toFixed(1)}%</td>
                <td>${r.ccStrike.toFixed(2)}</td>
                <td style={{ color: '#e67e22' }}>{r.ccAnnualizedYield.toFixed(1)}%</td>
                <td style={{ color: '#8e44ad', fontWeight: '600' }}>{r.cycleYield.toFixed(1)}%</td>
                <td style={{ fontWeight: '600' }}>{r.cyclesToZeroBasis != null ? `${r.cyclesToZeroBasis}x` : 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
          Generated {new Date(data.generatedAt).toLocaleString()} · CSP at 10% OTM · CC at upper Bollinger Band · Not financial advice
        </p>
      </div>
    );
  };

  const renderStraddleScreener = (data) => {
    if (!data || !data.results || data.results.length === 0) {
      return <div className="no-data-message">No stocks currently meet the Long Straddle screener criteria. Check back after market hours.</div>;
    }

    const formatExp = (dateStr, expDte) => {
      const d = new Date(dateStr + 'T00:00:00');
      return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (${expDte}d)`;
    };

    const straddleScoreColor = (score) => {
      if (score >= 75) return '#16a085';
      if (score >= 55) return '#1a8fd1';
      return '#7f8c8d';
    };

    return (
      <div className="stock-table-container">
        <h3>Long Straddle — S&amp;P 500</h3>
        <p className="screener-description">
          S&amp;P 500 stocks showing a Bollinger Band squeeze — compressed volatility that historically
          precedes large directional moves. Ranked by squeeze intensity (low BB width rank) and cheap
          implied vol (low HV rank). Buy the ATM call <em>and</em> ATM put; profit if the stock moves
          significantly in either direction before expiry. Premiums estimated via Black-Scholes using 30-day HV.
        </p>
        <div className="russell-badge">S&amp;P 500</div>
        <table className="stock-table clickable-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Symbol</th>
              <th>Company</th>
              <th>Price</th>
              <th>Straddle Score</th>
              <th>BB Squeeze</th>
              <th>HV Rank</th>
              <th>%B</th>
              <th>30d HV</th>
              <th>Strike</th>
              <th>Expiration</th>
              <th>Call</th>
              <th>Put</th>
              <th>Total Cost</th>
              <th>Breakeven %</th>
              <th>Up Target</th>
              <th>Down Target</th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((r, i) => (
              <tr key={r.symbol} onClick={() => handleStockClick(r.symbol)} className="clickable-row">
                <td style={{ color: '#aaa', fontSize: '12px' }}>{i + 1}</td>
                <td style={{ fontWeight: '700' }}>{r.symbol}</td>
                <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</td>
                <td>${r.currentPrice.toFixed(2)}</td>
                <td>
                  <span className="csp-score-badge" style={{ background: straddleScoreColor(r.straddleScore) }}>
                    {r.straddleScore}
                  </span>
                </td>
                <td>{r.bbWidthRank}th %ile</td>
                <td>{r.hvRank}th %ile</td>
                <td>{r.percentB.toFixed(2)}</td>
                <td>{(r.historicalVol30d * 100).toFixed(1)}%</td>
                <td>${r.atmStrike.toFixed(2)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatExp(r.suggestedExpiration, r.dte)}</td>
                <td style={{ color: '#27ae60' }}>${r.callPremium.toFixed(2)}</td>
                <td style={{ color: '#e74c3c' }}>${r.putPremium.toFixed(2)}</td>
                <td style={{ fontWeight: '600' }}>${r.straddleCost.toFixed(2)}</td>
                <td style={{ color: '#888' }}>&plusmn;{r.breakevenPct.toFixed(1)}%</td>
                <td style={{ color: '#27ae60', fontWeight: '600' }}>${r.breakevenUp.toFixed(2)}</td>
                <td style={{ color: '#e74c3c', fontWeight: '600' }}>${r.breakevenDown.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
          Generated {new Date(data.generatedAt).toLocaleString()} · ATM strike · HV Rank &lt; 50 · BB width in lower half of annual range · Not financial advice
        </p>
      </div>
    );
  };

  const renderPegScreener = (data) => {
    if (!data || !data.stocks || data.stocks.length === 0) {
      return <div className="no-data-message">No stocks found for this screener.</div>;
    }

    const formatMarketCap = (v) => {
      if (!v || v === 'N/A') return 'N/A';
      return v;
    };

    const pegColor = (peg) => {
      if (peg === null) return '#888';
      if (peg < 0.5) return '#1e8449';
      if (peg < 1.0) return '#27ae60';
      if (peg < 1.5) return '#e67e22';
      if (peg < 2.0) return '#e74c3c';
      return '#922b21';
    };

    return (
      <div className="stock-table-container">
        <h3>{data.strategy}</h3>
        <p className="screener-description">{data.description}</p>
        {data.formula && <p className="screener-formula"><strong>Formula:</strong> {data.formula}</p>}
        {indexMode === 'russell-2000' && <div className="russell-badge">Russell 2000</div>}
        {indexMode === 'sp500' && <div className="russell-badge">S&amp;P 500</div>}
        <table className="stock-table clickable-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Symbol</th>
              <th>Name</th>
              <th>Price</th>
              <th>Market Cap</th>
              <th>EPS</th>
              <th>P/E Ratio</th>
              <th>3yr EPS Growth</th>
              <th>PEG Ratio</th>
              <th>Category</th>
            </tr>
          </thead>
          <tbody>
            {data.stocks.map((stock, index) => (
              <tr key={stock.symbol} onClick={() => handleStockClick(stock.symbol)} className="clickable-row">
                <td style={{ color: '#aaa', fontSize: '12px' }}>{index + 1}</td>
                <td style={{ fontWeight: '700' }}>{stock.symbol}</td>
                <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stock.name}</td>
                <td>{stock.price != null ? `$${stock.price.toFixed(2)}` : 'N/A'}</td>
                <td>{formatMarketCap(stock.marketCap)}</td>
                <td>{stock.eps != null ? stock.eps.toFixed(2) : 'N/A'}</td>
                <td>{stock.peRatio != null ? stock.peRatio.toFixed(1) : 'N/A'}</td>
                <td>{stock.epsGrowth3yr != null ? `${stock.epsGrowth3yr.toFixed(1)}%` : 'N/A'}</td>
                <td style={{ fontWeight: '700', color: pegColor(stock.pegRatio) }}>
                  {stock.pegRatio != null ? stock.pegRatio.toFixed(2) : 'N/A'}
                </td>
                <td style={{ fontSize: '12px', color: pegColor(stock.pegRatio) }}>{stock.pegCategory || 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Handle changing the index mode
  const handleIndexModeChange = (mode) => {
    setIndexMode(mode);

    const currentScreener = screeners.find(s => s.id === activeScreener);

    if (mode === 'russell-2000') {
      if (!currentScreener || (!currentScreener.russellEndpoint && !currentScreener.russellOnly)) {
        setActiveScreener('top-value');
      }
    } else if (mode === 'sp500') {
      if (!currentScreener || !currentScreener.sp500Endpoint) {
        setActiveScreener('top-value');
      }
    } else if (mode === 'all') {
      if (currentScreener && currentScreener.russellOnly) {
        const defaultScreener = screeners.find(s => !s.russellOnly);
        if (defaultScreener) setActiveScreener(defaultScreener.id);
      }
    }
  };

  return (
    <div className="screeners-page">
      <div className="screener-controls-top">
        {/* Top-level mode toggle */}
        <div className="screener-mode-toggle">
          <button
            className={!isOptionsMode ? 'active' : ''}
            onClick={() => { if (isOptionsMode) setActiveScreener('top-value'); }}
          >
            Stock Screeners
          </button>
          {offerOptionsScreener && (
            <button
              className={isOptionsMode ? 'active' : ''}
              onClick={() => { if (!isOptionsMode) setActiveScreener('csp'); }}
            >
              Options Screeners
            </button>
          )}
        </div>

        {/* Index Selector — stock mode only */}
        {!isOptionsMode && (
          <div className="index-selector">
            <span className="mr-2 text-sm text-gray-600">Index:</span>
            <div className="index-selector-buttons">
              <button
                className={`px-3 py-1 text-sm font-medium ${indexMode === 'russell-2000' ? 'bg-primary-color text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                onClick={() => handleIndexModeChange('russell-2000')}
                style={{ backgroundColor: indexMode === 'russell-2000' ? '#3A2C70' : '', color: indexMode === 'russell-2000' ? 'white' : '' }}
              >
                Russell 2000
              </button>
              <button
                className={`px-3 py-1 text-sm font-medium ${indexMode === 'sp500' ? 'bg-primary-color text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                onClick={() => handleIndexModeChange('sp500')}
                style={{ backgroundColor: indexMode === 'sp500' ? '#3A2C70' : '', color: indexMode === 'sp500' ? 'white' : '' }}
              >
                S&amp;P 500
              </button>
              <button
                className={`px-3 py-1 text-sm font-medium ${indexMode === 'all' ? 'bg-primary-color text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                onClick={() => handleIndexModeChange('all')}
                style={{ backgroundColor: indexMode === 'all' ? '#3A2C70' : '', color: indexMode === 'all' ? 'white' : '' }}
              >
                All Markets
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Equity screener sub-tabs — stock mode only */}
      {!isOptionsMode && (
        <div className="screener-tabs">
          {visibleScreeners.map(screener => (
            <button
              key={screener.id}
              className={activeScreener === screener.id ? 'active' : ''}
              onClick={() => setActiveScreener(screener.id)}
            >
              {screener.name}
            </button>
          ))}
        </div>
      )}

      {/* Options screener sub-tabs */}
      {isOptionsMode && offerOptionsScreener && (
        <div className="screener-tabs">
          <button
            className={activeScreener === 'csp' ? 'active' : ''}
            onClick={() => setActiveScreener('csp')}
          >
            Cash Secured Puts
          </button>
          <button
            className={activeScreener === 'pds' ? 'active' : ''}
            onClick={() => setActiveScreener('pds')}
          >
            Put Debit Spreads
          </button>
          <button
            className={activeScreener === 'cc' ? 'active' : ''}
            onClick={() => setActiveScreener('cc')}
          >
            Covered Calls
          </button>
          <button
            className={activeScreener === 'wheel' ? 'active' : ''}
            onClick={() => setActiveScreener('wheel')}
          >
            Wheel Strategy
          </button>
          <button
            className={activeScreener === 'straddle' ? 'active' : ''}
            onClick={() => setActiveScreener('straddle')}
          >
            Long Straddle
          </button>
        </div>
      )}
      
      <div className="screener-content">
        {isOptionsMode ? (
          !canAccessCsp
            ? <PremiumPrompt feature="the Options screeners" inline />
            : activeScreener === 'csp'
              ? (cspLoading ? <LoadingSpinner /> : cspError ? <ErrorMessage message={cspError} /> : cspData && renderCspScreener(cspData))
              : activeScreener === 'pds'
                ? (pdsLoading ? <LoadingSpinner /> : pdsError ? <ErrorMessage message={pdsError} /> : pdsData && renderPdsScreener(pdsData))
                : activeScreener === 'cc'
                  ? (ccLoading ? <LoadingSpinner /> : ccError ? <ErrorMessage message={ccError} /> : ccData && renderCcScreener(ccData))
                  : activeScreener === 'wheel'
                    ? (wheelLoading ? <LoadingSpinner /> : wheelError ? <ErrorMessage message={wheelError} /> : wheelData && renderWheelScreener(wheelData))
                    : activeScreener === 'straddle'
                      ? (straddleLoading ? <LoadingSpinner /> : straddleError ? <ErrorMessage message={straddleError} /> : straddleData && renderStraddleScreener(straddleData))
                      : null
        ) : (
          <>
            {loading[getScreenerCacheKey(activeScreener)] && <LoadingSpinner />}
            {error[getScreenerCacheKey(activeScreener)] && <ErrorMessage message={error[getScreenerCacheKey(activeScreener)]} />}
            {!loading[getScreenerCacheKey(activeScreener)] &&
              !error[getScreenerCacheKey(activeScreener)] &&
              screenerData[getScreenerCacheKey(activeScreener)] && (
                activeScreener === 'top-value'
                  ? renderTopValue(screenerData[getScreenerCacheKey(activeScreener)])
                  : activeScreener === 'peg-ratio'
                    ? renderPegScreener(screenerData[getScreenerCacheKey(activeScreener)])
                    : renderStockTable(activeScreener, screenerData[getScreenerCacheKey(activeScreener)])
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScreenersPage;
